'use strict';

import { Buffer } from 'buffer';
import fs from 'fs';
import log from 'fancy-log';
import pluginError from 'plugin-error';
import through2 from 'through2';
import Vinyl from 'vinyl';
import * as util from '../utilities';

const PLUGIN_NAME = 'gulp-manifest-resource';

interface Options {
    /**
     * resource name.
     */
    resourceName: string;

    /**
     * path of manifest file.
     */
    manifest: string;

    /**
     * depth of folder structure from the locale folder. (default is 1)
     */
    localeOffset: number;
}

function gulpManifestResources(options: Options) {
    function findResourceKey(node: any, results: string[]): void {
        const match = 'resources:strings:';
        const keys = Object.keys(node);
        if (keys && keys.length > 0) {
            keys.forEach(key => {
                const target = node[key];
                if (target) {
                    if (typeof target === 'string') {
                        if (target.indexOf(match) === 0) {
                            const value = target.substring(match.length);
                            if (results.indexOf(value) < 0) {
                                results.push(value);
                            }
                        }
                    } else {
                        findResourceKey(target, results);
                    }
                }
            });
        }
    }

    // override options settings if not specified.
    options = <Options>Object.assign(
        {
            manifest: 'src/manifest.json',
            localeOffset: 1
        },
        options || {});

    const resources: any = {};

    return through2.obj(
        /**
         * Transform
         */
        function (file, encoding, callback) {
            let locale = 'default';
            if (resources.default) {
                // if default is set already, find current locale from the file path.
                const pathSegments = file.history[0].split('\\');
                locale = pathSegments[pathSegments.length - (2 + options.localeOffset)];
            }

            resources[locale] = {};
            if (resources.default) {
                // if default is set already, pre-fill all resource items from default.
                for (const resourceId in resources.default) {
                    if (resources.default.hasOwnProperty(resourceId)) {
                        resources[locale][resourceId] = resources.default[resourceId];
                    }
                }
            }

            // remove comments, /* multiline comment */ and // one line comment and "//": "JSON element comment"
            const content = file.contents.toString('utf8')
                                .replace(/(\/\*([^*]|[\n]|(\*+([^*/]|[\n])))*\*\/+)|( +\/\/.*)|(  +\"\/\/\".*)/g, '');
            const object = JSON.parse(content);
            if (object[options.resourceName] && object[options.resourceName]['Manifest']) {
                // supports <ResourceName>.Manifest.<KeyName> format.
                const items = object[options.resourceName]['Manifest'];
                for (const key in items) {
                    if (items.hasOwnProperty(key)) {
                        resources[locale][key] = items[key];
                    }
                }
            } else {
                // supports <ResourceName>_Manifest_KeyName format.
                const resourceManifest = options.resourceName + '_Manifest_';
                for (const name in object) {
                    if (object.hasOwnProperty(name)) {
                        const index = name.indexOf(resourceManifest);
                        if (index === 0) {
                            const value = object[name];
                            const resourceId2 = name.substring(resourceManifest.length);
                            resources[locale][resourceId2] = value;
                        }
                    }
                }
            }

            return callback();
        },

        /**
         * Flush
         */
        function (callback) {
            let result = null;
            try {
                const manifestObject = JSON.parse(fs.readFileSync(options.manifest, 'utf8'));

                const keys: string[] = [];
                findResourceKey(manifestObject, keys);
                const missing: string[] = [];
                const unused: string[] = Object.keys(resources.default);

                keys.forEach(function (key) {
                    const index = unused.indexOf(key);
                    if (index >= 0) {
                        unused.splice(index, 1);
                    } else {
                        missing.push(key);
                    }
                });

                if (missing.length > 0) {
                    missing.forEach(item => {
                        log.error('Missing resource: ' + item);
                    });
                    throw new Error('Missing resource found in manifest.json!');
                }

                if (unused.length > 0) {
                    unused.forEach(item => {
                        log.error('Unused resource: ' + item);
                    });
                    throw new Error('Unused resource found in strings.resjson!');
                }

                const locales = [];
                for (const locale in resources) {
                    if (resources.hasOwnProperty(locale) && locale !== 'default') {
                        locales.push(locale);
                    }
                }

                locales.sort();
                locales.unshift('default');
                const formattedResources = [];
                for (const locale of locales) {
                    formattedResources.push({
                        locale: locale,
                        strings: resources[locale]
                    });
                }

                manifestObject.resources = formattedResources;
                const manifestFile = new Vinyl({
                    cwd: './',
                    path: options.manifest,
                    contents: Buffer.from(JSON.stringify(manifestObject, null, 2), 'utf8')
                });

                this.push(manifestFile);
            } catch (e) {
                const error = (!e.plugin || (e.plugin !== PLUGIN_NAME)) ?
                    util.extendError(new pluginError({ plugin: PLUGIN_NAME, message: e.message }), e) : e;
                log.error(error);
                result = e;
            }

            callback(result);
        });
}

module.exports = gulpManifestResources;
